import { ServiceCollection, createInstanceDescriptor } from '@gitlab/needle';
import { DefaultWorkflowTokenService } from '@gitlab-org/workflow-executor';
import { RunGitCommandActionHandler } from '@gitlab-org/workflow-executor/node';
import { DefaultOSContextProvider } from '@gitlab-org/ai-context/node';
import type { ParsedCliInput } from '../../parse';
import { CliApiService } from '../../cli_api_service';
import { GitLabBackendFactory } from './gitlab_backend_factory';
import { GitLabModelManager } from './gitlab_model_manager';
import { RootNamespaceIdService } from './root_namespace_id_service';
import { GitLabSessionDataService } from './session_data_service';
import { PreConfiguredCliApiService } from './preconfigured_cli_api_service';
import {
  GitLabParsedOptions,
  isPreconfiguredCiRun,
  validateCiTokenConfiguration,
} from './gitlab_parsed_options';
import { PreConfiguredWorkflowTokenService } from './preconfigured_workflow_token_service';
import { OSInformationContextProvider } from './system_context/os_information';
import { ShellContextProvider } from './system_context/shell';
import { RunCondition, resolveRunCondition } from './run_condition';

/**
 * Registers the GitLab backend factory and all GitLab-specific services.
 *
 * @param serviceCollection - The service collection to register services with
 * @param backendOpts - The parsed GitLab-specific options
 * @param cliInput - The parsed CLI input (command + shared options)
 */
export function registerGitLabServices(
  serviceCollection: ServiceCollection,
  backendOpts: GitLabParsedOptions,
  cliInput: ParsedCliInput,
): void {
  const commandName = cliInput.command.name;

  // Register GitLabParsedOptions for DI injection
  serviceCollection.add(
    createInstanceDescriptor({
      instance: backendOpts,
      aliases: [GitLabParsedOptions],
    }),
  );

  // Resolve the run condition ONCE at startup — the single explicit fact that
  // drives all workflow-lifecycle decisions in the backend. Throws on a rails
  // run missing its injected workflow id (a contract violation surfaced eagerly
  // as a startup error).
  const injectedWorkflowId =
    commandName === 'run' || commandName === 'tui' ? cliInput.command.existingSessionId : undefined;
  const runCondition = resolveRunCondition(cliInput, backendOpts, injectedWorkflowId);
  serviceCollection.add(
    createInstanceDescriptor({
      instance: runCondition,
      aliases: [RunCondition],
    }),
  );

  serviceCollection.addClass(GitLabModelManager);
  serviceCollection.addClass(RootNamespaceIdService);
  serviceCollection.addClass(GitLabBackendFactory);
  serviceCollection.addClass(GitLabSessionDataService);
  serviceCollection.addClass(RunGitCommandActionHandler);

  const isHeadlessRunCIMode = isPreconfiguredCiRun(commandName, backendOpts);

  if (isHeadlessRunCIMode) {
    // Validate CI token configuration eagerly during registration
    validateCiTokenConfiguration(backendOpts);

    serviceCollection.addClass(PreConfiguredWorkflowTokenService, PreConfiguredCliApiService);

    serviceCollection.addClass(OSInformationContextProvider);
  } else {
    serviceCollection.addClass(DefaultWorkflowTokenService, CliApiService);
  }

  // Backend requires OS info as XML rather than JSON, with a different `category` for headless runs
  if (!isHeadlessRunCIMode) {
    serviceCollection.addClass(
      commandName === 'run' ? OSInformationContextProvider : DefaultOSContextProvider,
    );
  }

  // Shell context (cwd + shell type) is useful in all modes
  serviceCollection.addClass(ShellContextProvider);
}
